import { TableColumn } from '@/components/common/DataTable';
import type { TaskFilterOption } from '@/services/api/tasksService';
import { Task } from '@/services/types/client-intake-types';

import { FILTER_CONFIG_KEYS, TableColumnIds } from '../config/filterConfig';
import { getSortedOptions } from '../utils/filterUtils';
import {
  createAgentColumn,
  createBaseTaskColumns,
  StandardColumnOptions,
} from './standardColumns';

// ============================================================================
// Vertical Column Strategies
// ============================================================================
// This file centralizes the column definitions for standard task verticals.
// For complex verticals with many custom columns, a separate file is recommended.
// ============================================================================

// --- Factory Type ---
export type ColumnsFactory = (
  options: StandardColumnOptions,
) => TableColumn<Task>[];

// --- Custom Columns ---

const createPoliceDepartmentColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.POLICE_DEPARTMENT,
  header: 'Police Department',
  accessorKey: 'police_departments',
  width: '200px',
  cell: (row) => {
    const depts = row.police_departments || [];
    if (depts.length === 0) {
      return <span className="text-gray-400">—</span>;
    }
    const allNames = depts.map((d) => d.name).join(', ');
    const displayText =
      depts.length === 1
        ? depts[0].name
        : `${depts[0].name} +${depts.length - 1}`;
    return (
      <span
        className="text-gray-700 dark:text-zinc-300 truncate"
        title={allNames}
      >
        {displayText}
      </span>
    );
  },
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(
          FILTER_CONFIG_KEYS.POLICE_DEPARTMENT,
          options,
        ),
        searchable: true,
      }
    : undefined,
});

const createFacilityColumn = (
  options?: TaskFilterOption[],
): TableColumn<Task> => ({
  id: TableColumnIds.FACILITY,
  header: 'Facility',
  accessorKey: 'medical_note',
  width: '200px',
  cell: (row) => {
    const facilityName = row.medical_note?.name || '—';
    return (
      <span
        className="text-gray-700 dark:text-zinc-300 truncate"
        title={facilityName}
      >
        {facilityName}
      </span>
    );
  },
  filter: options
    ? {
        type: 'multiselect',
        options: getSortedOptions(FILTER_CONFIG_KEYS.MEDICAL_PROVIDER, options),
        searchable: true,
      }
    : undefined,
});

// --- Column Strategies ---

export const VERTICAL_COLUMN_FACTORIES: Record<string, ColumnsFactory> = {
  // Claims: Base columns plus Agent status
  claims: (options) => {
    const columns = createBaseTaskColumns(options);
    if (columns.some((column) => column.id === TableColumnIds.AGENT)) {
      return columns;
    }
    const statusIndex = columns.findIndex(
      (column) => column.id === TableColumnIds.TASK_STATUS,
    );
    if (statusIndex === -1) {
      return [createAgentColumn(options.agentStatusOptions), ...columns];
    }
    return [
      ...columns.slice(0, statusIndex + 1),
      createAgentColumn(options.agentStatusOptions),
      ...columns.slice(statusIndex + 1),
    ];
  },

  // Liability: Adds Police Department column
  liability: (options) =>
    createBaseTaskColumns(options, [
      createPoliceDepartmentColumn(options.policeDepartmentOptions),
    ]),

  // Medical: Adds Facility column
  medical: (options) =>
    createBaseTaskColumns(options, [
      createFacilityColumn(options.medicalProviderOptions),
    ]),

  // Standard verticals (Base columns only)
  demand: (options) => createBaseTaskColumns(options),
  intake: (options) => createBaseTaskColumns(options),
  miscellaneous: (options) => createBaseTaskColumns(options),
  all: (options) => createBaseTaskColumns(options),
};
